import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Button,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Card,
  Statistic,
  message,
  Select,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useArticleCategories } from '../../../hooks/useContent';
import type { ArticleCategory, CreateArticleCategoryDto, UpdateArticleCategoryDto } from '../../../services/content.service';
import { tenantService } from '../../../services/tenant.service';
import type { Tenant } from '../../../services/tenant.service';

import { CommonState } from '@erpgold/shared';
const { Title } = Typography;

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: DraggableSyntheticListeners;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'grab', color: '#bfbfbf' }}
      ref={setActivatorNodeRef as React.Ref<HTMLButtonElement>}
      {...listeners}
    />
  );
};

interface SortableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const SortableRow: React.FC<SortableRowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, opacity: 0.8 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const statusColors: Record<number, string> = {
  [CommonState.ENABLED]: 'success',
  [CommonState.DISABLED]: 'error',
};

export const ArticleCategoryList: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const [localFlatCategories, setLocalFlatCategories] = useState<ArticleCategory[]>([]);

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantFilter, setTenantFilterLocal] = useState('');

  useEffect(() => {
    tenantService.getTenants({ limit: 100 }).then((res) => setTenants(res.data)).catch(() => { });
  }, []);

  const {
    categories,
    loading,
    createCategory,
    updateCategory,
    deleteCategories,
    updateOrdering,
    setTenantFilter,
  } = useArticleCategories();

  const handleOpenModal = (category?: ArticleCategory) => {
    if (category) {
      setEditingId(category.category_id || category._id);
      form.setFieldsValue({
        name: category.name,
        parent_id: category.parent_id,
        description: category.description,
        image_url: category.image_url,
        ordering: category.ordering,
        state: category.state,
        metadata: category.metadata,
      });
      setParentId(category.parent_id ?? undefined);
    } else {
      setEditingId(null);
      form.resetFields();
      setParentId(undefined);
    }
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingId(null);
    form.resetFields();
    setParentId(undefined);
  };

  const handleSubmit = async (values: CreateArticleCategoryDto & UpdateArticleCategoryDto) => {
    try {
      const data = { ...values, metadata: values.metadata ?? {} };
      if (editingId) {
        await updateCategory(editingId, data);
        message.success('Category updated successfully');
      } else {
        await createCategory(data as CreateArticleCategoryDto);
        message.success('Category created successfully');
      }
      handleCloseModal();
    } catch (error: unknown) {
      const msg = (error as { message?: string })?.message;
      message.error(msg || (editingId ? 'Failed to update category' : 'Failed to create category'));
    }
  };

  const handleDeleteMany = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select categories to delete');
      return;
    }
    Modal.confirm({
      title: 'Delete Categories',
      content: `Are you sure you want to delete ${selectedRowKeys.length} category(s)?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteCategories(selectedRowKeys as string[]);
          setSelectedRowKeys([]);
          message.success('Categories deleted successfully');
        } catch (error: any) {
          message.error(error?.message || 'Failed to delete categories');
        }
      },
    });
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const flattenCategories = (cats: ArticleCategory[], level = 0): ArticleCategory[] => {
    const result: ArticleCategory[] = [];
    for (const cat of cats) {
      const { children, ...rest } = cat;
      result.push({ ...rest, level: level + 1 });
      if (children && children.length > 0) {
        result.push(...flattenCategories(children, level + 1));
      }
    }
    return result;
  };

  useEffect(() => {
    setLocalFlatCategories(flattenCategories(categories));
  }, [categories]);

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const activeIndex = localFlatCategories.findIndex((i) => (i.category_id || i._id) === active.id);
    const overIndex = localFlatCategories.findIndex((i) => (i.category_id || i._id) === over.id);
    const reordered = arrayMove(localFlatCategories, activeIndex, overIndex);
    setLocalFlatCategories(reordered);
    try {
      await updateOrdering(reordered.map((cat, idx) => ({ _id: cat._id, ordering: idx + 1 })));
    } catch {
      message.error('Failed to save ordering');
      setLocalFlatCategories(flattenCategories(categories));
    }
  };

  const columns: ColumnsType<ArticleCategory> = [
    {
      key: 'sort',
      width: 40,
      render: () => <DragHandle />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ArticleCategory) => {
        const level = (record.level ?? 1) - 1;
        if (level === 0) return <span>{text}</span>;
        return (
          <span>
            <span style={{ color: '#bfbfbf', marginRight: 4, userSelect: 'none' }}>
              {'|' + '———'.repeat(level)}
            </span>
            {text}
          </span>
        );
      },
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      width: 200,
    },
    {
      title: 'Articles',
      dataIndex: 'article_count',
      key: 'article_count',
      width: 100,
      render: (count: number) => count || 0,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state: number) => (
        <Tag color={statusColors[state] || 'default'}>
          {state === CommonState.ENABLED ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Ordering',
      dataIndex: 'ordering',
      key: 'ordering',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: unknown, record: ArticleCategory) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: 'Delete Category',
                content: `Are you sure you want to delete "${record.name}"?`,
                okText: 'Delete',
                okType: 'danger',
                onOk: async () => {
                  try {
                    await deleteCategories([record.category_id || record._id]);
                    message.success('Category deleted successfully');
                  } catch (error: unknown) {
                    message.error((error as { message?: string })?.message || 'Failed to delete category');
                  }
                },
              });
            }}
          />
        </Space>
      ),
    },
  ];

  const flattenCategoriesForSelect = (cats: ArticleCategory[], prefix = ''): { label: string; value: string }[] => {
    const result: { label: string; value: string }[] = [];
    for (const cat of cats) {
      result.push({
        label: `${prefix}${cat.name}`,
        value: cat.category_id || cat._id,
      });
      if (cat.children && cat.children.length > 0) {
        result.push(...flattenCategoriesForSelect(cat.children, `${prefix}-- `));
      }
    }
    return result;
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic title="Total Categories" value={localFlatCategories.length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active"
              value={localFlatCategories.filter((c) => c.state === CommonState.ENABLED).length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Inactive"
              value={localFlatCategories.filter((c) => c.state === CommonState.DISABLED).length}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input.Search
                placeholder="Lọc theo Tenant ID"
                value={tenantFilter}
                onChange={(e) => setTenantFilterLocal(e.target.value)}
                onSearch={(value) => setTenantFilter(value)}
                allowClear
                onClear={() => setTenantFilter('')}
                style={{ maxWidth: 320 }}
              />
            </Col>
            <Col>
              <Space>
                {selectedRowKeys.length > 0 && (
                  <Button danger icon={<DeleteOutlined />} onClick={handleDeleteMany}>
                    Delete Selected ({selectedRowKeys.length})
                  </Button>
                )}
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
                  Add Category
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localFlatCategories.map((c) => c.category_id || c._id)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              dataSource={localFlatCategories}
              columns={columns}
              rowKey={(record) => record.category_id || record._id}
              loading={loading}
              pagination={false}
              scroll={{ x: 800 }}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              components={{ body: { row: SortableRow } }}
            />
          </SortableContext>
        </DndContext>
      </Card>

      <Modal
        title={editingId ? 'Edit Category' : 'Create Category'}
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
            <Input placeholder="Enter category name" />
          </Form.Item>

          {!editingId && (
            <Form.Item name="tenant_id" label="Tenant" rules={[{ required: true, message: 'Please select a tenant' }]}>
              <Select
                showSearch
                placeholder="Select tenant"
                options={tenants.map((t) => ({ label: `${t.name} (${t.id})`, value: t.id }))}
              />
            </Form.Item>
          )}

          <Form.Item name="parent_id" label="Parent Category">
            <Select
              placeholder="Select parent category (optional)"
              allowClear
              value={parentId}
              onChange={(value) => setParentId(value)}
            >
              {flattenCategoriesForSelect(categories).map((cat) => (
                <Select.Option key={cat.value} value={cat.value}>{cat.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Category description" />
          </Form.Item>

          <Form.Item name="image_url" label="Image URL">
            <Input placeholder="Category image URL" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ordering" label="Ordering">
                <Input type="number" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="Status">
                <Select>
                  <Select.Option value={CommonState.ENABLED}>Enabled</Select.Option>
                  <Select.Option value={CommonState.DISABLED}>Disabled</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Title level={5}>SEO Information</Title>
          <Form.Item name={['metadata', 'seo_title']} label="SEO Title">
            <Input placeholder="SEO title" />
          </Form.Item>
          <Form.Item name={['metadata', 'seo_description']} label="SEO Description">
            <Input.TextArea rows={2} placeholder="SEO description" />
          </Form.Item>
          <Form.Item name={['metadata', 'seo_keywords']} label="SEO Keywords">
            <Input placeholder="SEO keywords" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ArticleCategoryList;